// client/src/pages/ComparePage.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Box, Button, IconButton, CircularProgress,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CloseIcon from '@mui/icons-material/Close';
import { CartContext } from '../context/CartContext';
import { formatCurrency } from '../utils/currency';
import { API_BASE_URL, getProductImageUrl } from '../utils/media';
import { getCompareIds, toggleCompare } from '../utils/compareList';
import EmptyState from '../components/EmptyState';
import { useGeo } from '../context/GeoContext';

const ComparePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useContext(CartContext);
  const { formatLocalPrice } = useGeo();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const ids = getCompareIds();
    const results = await Promise.all(
      ids.map((id) => fetch(`${API_BASE_URL}/api/products/${id}`).then((r) => (r.ok ? r.json() : null)).catch(() => null))
    );
    setProducts(results.filter(Boolean).map((r) => r.product));
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleRemove = (id) => {
    toggleCompare(id);
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  if (loading) return <Container sx={{ py: 4 }}><CircularProgress size={28} /></Container>;

  if (products.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <EmptyState
          icon={<CompareArrowsIcon />}
          title="No products to compare yet"
          description="Use the compare icon on any product card to add it here, then come back to compare side by side."
          actionLabel="Continue Shopping"
          actionTo="/"
        />
      </Container>
    );
  }

  const rows = [
    {
      label: 'Price',
      render: (p) => {
        const local = formatLocalPrice(p.price);
        return local ? `${formatCurrency(p.price)} (≈ ${local})` : formatCurrency(p.price);
      },
    },
    { label: 'Category', render: (p) => p.subcategory ? `${p.category} / ${p.subcategory}` : (p.category || '—') },
    { label: 'In Stock', render: (p) => (Number(p.quantity) > 0 ? `${p.quantity} available` : 'Out of stock') },
    { label: 'Description', render: (p) => p.description || '—' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Compare Products</Typography>
      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              {products.map((p) => (
                <TableCell key={p.id} align="center" sx={{ minWidth: 220 }}>
                  <IconButton size="small" onClick={() => handleRemove(p.id)} sx={{ float: 'right' }} aria-label="Remove from compare">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <Box component={Link} to={`/products/${p.id}`}>
                    <Box component="img" src={getProductImageUrl(p.imageUrl)} alt={p.name} sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700} component={Link} to={`/products/${p.id}`} sx={{ color: 'text.primary', textDecoration: 'none' }}>
                    {p.name}
                  </Typography>
                  <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={() => addToCart(p)} disabled={Number(p.quantity) <= 0}>
                    Add to Cart
                  </Button>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                {products.map((p) => (
                  <TableCell key={p.id} align="center">{row.render(p)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default ComparePage;
